import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import {
  Alert,
  Button,
  Icons,
  Input,
  Label,
  Textarea,
  useToggle,
} from '@commenda/kakashi';
import { WorkflowStepType } from '@commenda/types';
import { copyToClipboard } from '@commenda/utils';
import { useReactFlow } from '@xyflow/react';
import { useState } from 'react';
import { useNotification } from '../../../hooks/useNotification';
import { JsonSchemaEditorModal } from '../../../json-schema-builder/components/modals/JsonSchemaEditorModal';
import { WorkflowNodeHelper } from '../../../workflow/helpers/node';
import { useWorkflow } from '../../context/WorkflowContext';
import { TSelectedEntity } from '../../types/node';
import { AgentNodePropertyPanel } from './AgentNodePropertyPanel';
import { ApiCallNodePropertyPanel } from './ApiCallNodePropertyPanel';
import { DemoAIFillingAgentNodePropertyPanel } from './DemoAIFillingAgentNodePropertyPanel';
import { DemoEmailNodePropertyPanel } from './EmailNodePropertyPanel';
import { EsignNodePropertyPanel } from './EsignNodePropertyPanel';
import { EstimatedTimeToCompleteConfig } from './EstimatedTimeToCompleteConfig';
import { HelpResourcesConfig } from './HelpResourcesConfig';
import { HumanFormNodePropertyPanel } from './HumanFormNodePropertyPanel';
import { HumanReviewNodePropertyPanel } from './HumanReviewNodePropertyPanel';
import { HumanStepAssigneeConfig } from './HumanStepAssigneeConfig';
import { LegacyServiceRequestProperty } from './LegacyServiceRequestProperty';
import { LLMNodePropertyPanel } from './LLMNodePropertyPanel';
import { RedirectNodePropertyPanel } from './RedirectNodePropertyPanel';
import { RootfiSyncNodePropertyPanel } from './RootfiSyncNodePropertyPanel';
import { StepEventHandlersConfig } from './StepEventHandlersConfig';
import { SubWorkflowNodePropertyPanel } from './SubWorkflowNodePropertyPanel';
import { SysEmailNodePropertyPanel } from './SysEmailNodePropertyPanel';
import { ToolCallNodePropertyPanel } from './ToolCallNodePropertyPanel';
import { TrackingCategoryProperty } from './TrackingCategoryProperty';
import { WatchJobNodePropertyPanel } from './WatchJobNodePropertyPanel';

export const NodePropertyPanel = ({
  selectedEntity,
}: {
  selectedEntity: TSelectedEntity;
}) => {
  const { setNotification } = useNotification();
  const { workflow, setSelectedEntities, setWorkflow } = useWorkflow();
  const { deleteElements } = useReactFlow();
  const jsonSchemaModalVisibility = useToggle();

  const selectedStep = workflow?.steps.find(
    (step) => step.id === selectedEntity.id
  );

  const [outputSchema, setOutputSchema] = useState<Record<string, any> | null>(
    selectedStep?.outputSchema ? selectedStep?.outputSchema : null
  );

  const isOutputSchemaApplicable = !WorkflowNodeHelper.isGatewayNode(
    selectedStep?.type
  );

  const isLegacyServiceRequestApplicable = !WorkflowNodeHelper.isGatewayNode(
    selectedStep?.type
  );

  const isTrackingCategoryApplicable =
    !WorkflowNodeHelper.isGatewayNode(selectedStep?.type) &&
    isFeatureLaunched(FeatureLaunch.SLA_CATEGORY_EXPORT);

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <span className="txt-medium-medium">Properties</span>
        <Button
          variant="tertiary"
          size="sm"
          onClick={() => {
            deleteElements({ nodes: [{ id: selectedEntity.id }] });
            setSelectedEntities([]);
          }}
        >
          <Icons.Trash01 className="size-5" />
        </Button>
      </div>
      <div className="flex gap-2 flex-col">
        <Label>Step Id</Label>
        <div className="flex items-center gap-2">
          <Input
            value={selectedEntity.id}
            disabled
            className="grow border-none bg-white"
          />
          <Button
            variant="tertiary"
            onClick={() => {
              copyToClipboard(selectedEntity.id);
              setNotification({
                show: true,
                valid: true,
                title: 'Copied',
                message: 'Step ID copied to clipboard',
              });
            }}
          >
            <Icons.Copy01 className="size-4" />
          </Button>
        </div>
      </div>
      {/* SYS_ROOTFI_SYNC renders heading + description at runtime per
          integration state — see WorkflowRootfiSyncTask. */}
      {selectedStep?.type !== WorkflowStepType.SYS_ROOTFI_SYNC && (
        <>
          <div className="flex gap-2 flex-col">
            <Label>Name</Label>
            <Input
              value={selectedStep?.name || ''}
              onChange={(e) => {
                setWorkflow({
                  ...workflow,
                  steps: workflow?.steps.map((step) =>
                    step.id === selectedEntity.id
                      ? { ...step, name: e.target.value }
                      : step
                  ),
                });
              }}
            />
          </div>
          <div className="flex gap-2 flex-col">
            <Label>Description</Label>
            <Textarea
              value={selectedStep?.description || ''}
              placeholder="Describe what this step does..."
              onChange={(e) => {
                setWorkflow({
                  ...workflow,
                  steps: workflow?.steps.map((step) =>
                    step.id === selectedEntity.id
                      ? { ...step, description: e.target.value }
                      : step
                  ),
                });
              }}
            />
          </div>
        </>
      )}
      {WorkflowNodeHelper.isHumanNode(selectedStep?.type) && (
        <>
          <HumanStepAssigneeConfig selectedEntity={selectedEntity} />
          <EstimatedTimeToCompleteConfig selectedEntity={selectedEntity} />
          <StepEventHandlersConfig selectedEntity={selectedEntity} />
        </>
      )}
      {isOutputSchemaApplicable && (
        <div className="flex gap-2 flex-col">
          <Label>Output Schema</Label>
          {outputSchema ? (
            <Alert
              variant="info"
              description="This step outputs data. You can edit the output schema to customize the data that is outputted."
            />
          ) : null}
          <Button
            variant="secondary"
            size="sm"
            onClick={jsonSchemaModalVisibility.show}
          >
            {outputSchema ? (
              <>
                <Icons.Edit02 className="size-4" />
                Edit
              </>
            ) : (
              <>
                <Icons.Plus className="size-4" />
                Add
              </>
            )}
          </Button>
        </div>
      )}
      {isLegacyServiceRequestApplicable && (
        <LegacyServiceRequestProperty selectedEntity={selectedEntity} />
      )}
      {isTrackingCategoryApplicable && (
        <TrackingCategoryProperty selectedEntity={selectedEntity} />
      )}
      {selectedStep?.type === WorkflowStepType.SYS_SUB_WORKFLOW && (
        <SubWorkflowNodePropertyPanel selectedEntity={selectedEntity} />
      )}
      {selectedStep?.type === WorkflowStepType.SYS_API_CALL && (
        <ApiCallNodePropertyPanel selectedEntity={selectedEntity} />
      )}
      {selectedStep?.type === WorkflowStepType.SYS_AI_AGENT && (
        <AgentNodePropertyPanel selectedEntity={selectedEntity} />
      )}
      {selectedStep?.type === WorkflowStepType.SYS_TOOL_CALL && (
        <ToolCallNodePropertyPanel selectedEntity={selectedEntity} />
      )}
      {selectedStep?.type === WorkflowStepType.SYS_WATCH_JOB && (
        <WatchJobNodePropertyPanel selectedEntity={selectedEntity} />
      )}
      {selectedStep?.type === WorkflowStepType.SYS_ROOTFI_SYNC && (
        <RootfiSyncNodePropertyPanel selectedEntity={selectedEntity} />
      )}
      {selectedStep?.type === WorkflowStepType.HUMAN_FORM && (
        <HumanFormNodePropertyPanel selectedEntity={selectedEntity} />
      )}
      {selectedStep?.type === WorkflowStepType.HUMAN_REDIRECT && (
        <RedirectNodePropertyPanel selectedEntity={selectedEntity} />
      )}
      {selectedStep?.type === WorkflowStepType.SYS_LLM && (
        <LLMNodePropertyPanel selectedEntity={selectedEntity} />
      )}
      {selectedStep?.type === WorkflowStepType.SYS_EMAIL && (
        <SysEmailNodePropertyPanel selectedEntity={selectedEntity} />
      )}
      {selectedStep?.type === WorkflowStepType.DEMO_EMAIL && (
        <DemoEmailNodePropertyPanel selectedEntity={selectedEntity} />
      )}
      {selectedStep?.type === WorkflowStepType.DEMO_AI_FILLING_AGENT && (
        <DemoAIFillingAgentNodePropertyPanel selectedEntity={selectedEntity} />
      )}
      {selectedStep?.type === WorkflowStepType.HUMAN_REVIEW && (
        <HumanReviewNodePropertyPanel selectedEntity={selectedEntity} />
      )}
      {selectedStep?.type === WorkflowStepType.HUMAN_ESIGN && (
        <EsignNodePropertyPanel selectedEntity={selectedEntity} />
      )}
      <HelpResourcesConfig selectedEntity={selectedEntity} />
      <JsonSchemaEditorModal
        isOpen={jsonSchemaModalVisibility.visible}
        onClose={jsonSchemaModalVisibility.hide}
        jsonSchema={outputSchema}
        setJsonSchema={(jsonSchema) => {
          setOutputSchema(jsonSchema);
          setWorkflow({
            ...workflow,
            steps: workflow?.steps.map((step) =>
              step.id === selectedEntity.id
                ? { ...step, outputSchema: jsonSchema }
                : step
            ),
          });
          jsonSchemaModalVisibility.hide();
        }}
      />
    </div>
  );
};
